import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { motion } from 'framer-motion';
import { ExternalLink, Layers, Pencil, Plus, Trash2, Upload, X } from 'lucide-react';
import { clearPortalQueryCache } from '../utils/portalQueryCache';
import { revealItemTransition } from '../utils/motionReveal';

const CATALOG_URL = '/api/admin/platform-catalog';
const DESCRIPTION_MAX = 500;
const SCREENSHOT_MAX_MB = 5;
const PLATFORM_CODE_PATTERN = /^[a-z0-9_-]{1,64}$/;
/** Matches tool-card / admin preview frame (2:1). Canvas is export-sized; CSS scales it down. */
const SCREENSHOT_CROP_W = 1200;
const SCREENSHOT_CROP_H = 600;

function drawCoverCrop(canvas, img, zoom, pan) {
  const ctx = canvas.getContext('2d');
  if (!ctx || !img?.width) return;

  const targetW = canvas.width;
  const targetH = canvas.height;
  ctx.clearRect(0, 0, targetW, targetH);
  ctx.fillStyle = '#111';
  ctx.fillRect(0, 0, targetW, targetH);

  const baseScale = Math.max(targetW / img.width, targetH / img.height);
  const scale = baseScale * zoom;
  const drawW = img.width * scale;
  const drawH = img.height * scale;
  const baseDx = (targetW - drawW) / 2;
  const baseDy = (targetH - drawH) / 2;
  const minDx = targetW - drawW;
  const maxDx = 0;
  const minDy = targetH - drawH;
  const maxDy = 0;
  const dx = Math.min(maxDx, Math.max(minDx, baseDx + (pan?.x || 0)));
  const dy = Math.min(maxDy, Math.max(minDy, baseDy + (pan?.y || 0)));

  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'high';
  ctx.drawImage(img, dx, dy, drawW, drawH);
}

function clampCoverPan(canvas, img, zoom, pan) {
  const targetW = canvas.width;
  const targetH = canvas.height;
  const baseScale = Math.max(targetW / img.width, targetH / img.height);
  const scale = baseScale * zoom;
  const drawW = img.width * scale;
  const drawH = img.height * scale;
  const baseDx = (targetW - drawW) / 2;
  const baseDy = (targetH - drawH) / 2;
  const minPanX = targetW - drawW - baseDx;
  const maxPanX = 0 - baseDx;
  const minPanY = targetH - drawH - baseDy;
  const maxPanY = 0 - baseDy;
  return {
    x: Math.min(maxPanX, Math.max(minPanX, pan?.x || 0)),
    y: Math.min(maxPanY, Math.max(minPanY, pan?.y || 0)),
  };
}

function canvasToOptimizedBlob(canvas) {
  // Prefer JPEG: System.Drawing/GDI+ on the server cannot reliably decode canvas WebP.
  // Cropped 1200×600 JPEG at ~0.85 is already a compact card thumbnail.
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (jpegBlob) => {
        if (jpegBlob && jpegBlob.size > 0) {
          resolve({ blob: jpegBlob, fileName: 'screenshot.jpg', contentType: 'image/jpeg' });
          return;
        }
        reject(new Error('Unable to prepare screenshot image.'));
      },
      'image/jpeg',
      0.85
    );
  });
}

async function exportScreenshotCrop(img, zoom, pan) {
  const exportCanvas = document.createElement('canvas');
  exportCanvas.width = SCREENSHOT_CROP_W;
  exportCanvas.height = SCREENSHOT_CROP_H;
  const clamped = clampCoverPan(exportCanvas, img, zoom, pan);
  drawCoverCrop(exportCanvas, img, zoom, clamped);
  const optimized = await canvasToOptimizedBlob(exportCanvas);
  if (!optimized?.blob) {
    throw new Error('Unable to prepare screenshot image.');
  }
  return new File([optimized.blob], optimized.fileName, { type: optimized.contentType });
}

const LOGO_THUMB_SIZE = 256;

function loadImageFromFile(file) {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve(img);
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('Unable to load logo image.'));
    };
    img.src = url;
  });
}

/** Square PNG thumbnail for the launcher widget (server re-encodes to 128px). */
async function exportLogoThumbnail(file) {
  const img = await loadImageFromFile(file);
  const canvas = document.createElement('canvas');
  canvas.width = LOGO_THUMB_SIZE;
  canvas.height = LOGO_THUMB_SIZE;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Unable to prepare logo thumbnail.');
  ctx.clearRect(0, 0, LOGO_THUMB_SIZE, LOGO_THUMB_SIZE);
  const scale = Math.min(LOGO_THUMB_SIZE / img.width, LOGO_THUMB_SIZE / img.height);
  const drawW = img.width * scale;
  const drawH = img.height * scale;
  const dx = (LOGO_THUMB_SIZE - drawW) / 2;
  const dy = (LOGO_THUMB_SIZE - drawH) / 2;
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'high';
  ctx.drawImage(img, dx, dy, drawW, drawH);
  const blob = await new Promise((resolve) => canvas.toBlob(resolve, 'image/png'));
  if (!blob) throw new Error('Unable to prepare logo thumbnail.');
  return new File([blob], 'logo.png', { type: 'image/png' });
}

function normalizePlatformCode(value) {
  return String(value || '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9_-]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 64);
}

function slugifyPlatformCode(name) {
  const normalized = normalizePlatformCode(
    String(name || '')
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
  );
  if (!normalized) return '';
  return normalized;
}

function isValidPlatformCode(code) {
  return PLATFORM_CODE_PATTERN.test(String(code || '').trim());
}

function screenshotUrlFor(platform, cacheBust = '') {
  const fromApi = String(platform?.screenshotUrl || '').trim();
  if (fromApi) {
    return cacheBust ? `${fromApi}${fromApi.includes('?') ? '&' : '?'}v=${encodeURIComponent(cacheBust)}` : fromApi;
  }

  const code = String(platform?.code || '').trim().toLowerCase();
  const fallback = String(platform?.defaultScreenshotUrl || '/platform-screenshots/_default.jpg').trim();
  if (!code) return fallback;
  const base = `/platform-screenshots/${encodeURIComponent(code)}.jpg`;
  return cacheBust ? `${base}?v=${encodeURIComponent(cacheBust)}` : base;
}

function screenshotFileName(code) {
  const normalized = String(code || '').trim().toLowerCase();
  return normalized ? `${normalized}.jpg` : 'your-code.jpg';
}

function logoUrlFor(platform, cacheBust = '') {
  const fromApi = String(platform?.logoUrl || '').trim();
  if (fromApi) {
    return cacheBust ? `${fromApi}${fromApi.includes('?') ? '&' : '?'}v=${encodeURIComponent(cacheBust)}` : fromApi;
  }

  const code = String(platform?.code || '').trim().toLowerCase();
  const fallback = String(platform?.defaultLogoUrl || '/platform-logos/_default.png').trim();
  if (!code) return fallback;
  const base = `/platform-logos/${encodeURIComponent(code)}.png`;
  return cacheBust ? `${base}?v=${encodeURIComponent(cacheBust)}` : base;
}

function logoFileName(code) {
  const normalized = String(code || '').trim().toLowerCase();
  return normalized ? `${normalized}.png` : 'your-code.png';
}

const emptyForm = () => ({
  name: '',
  code: '',
  description: '',
  url: '',
  isActive: true,
  dataAreaUrls: [],
  imageFile: null,
  imagePreview: '',
  logoFile: null,
  logoPreview: '',
  codeTouched: false,
});

function formFromPlatform(platform, previewCacheBust) {
  const dataAreaUrls = Array.isArray(platform?.dataAreaUrls)
    ? platform.dataAreaUrls.map((row) => ({
        dataAreaId: String(row?.dataAreaId || '').trim(),
        url: String(row?.url || '').trim(),
      }))
    : [];

  return {
    name: platform.name || '',
    code: platform.code || '',
    description: platform.description || '',
    url: platform.url || '',
    isActive: platform.isActive !== false,
    dataAreaUrls,
    imageFile: null,
    imagePreview: platform.hasScreenshot || platform.screenshotUrl ? screenshotUrlFor(platform, previewCacheBust) : '',
    logoFile: null,
    logoPreview: platform.hasLogo || platform.logoUrl ? logoUrlFor(platform, previewCacheBust) : '',
    codeTouched: true,
  };
}

const PlatformScreenshotImage = React.memo(function PlatformScreenshotImage({ platform, cacheBust }) {
  const [failed, setFailed] = useState(false);
  const src = useMemo(() => screenshotUrlFor(platform, cacheBust), [platform, cacheBust]);

  if (failed) {
    return (
      <div className="admin-platform-card-media-fallback" aria-hidden="true">
        <Layers size={28} />
      </div>
    );
  }

  return (
    <img
      src={src}
      alt=""
      loading="lazy"
      decoding="async"
      onError={() => setFailed(true)}
    />
  );
});

const PlatformCardPreview = React.memo(function PlatformCardPreview({ name, code, description, imagePreview }) {
  const displayName = name.trim() || 'Platform name';
  const displayCode = code.trim() || 'platform-code';
  const displayDescription = description.trim() || 'Description preview';

  return (
    <div className="admin-platform-live-preview" aria-hidden="true">
      <div className="admin-platform-live-preview-media">
        {imagePreview ? (
          <img src={imagePreview} alt="" decoding="async" />
        ) : (
          <div className="admin-platform-live-preview-placeholder" />
        )}
        <div className="admin-platform-live-preview-chrome" aria-hidden="true">
          <span className="dot red" />
          <span className="dot yellow" />
          <span className="dot green" />
        </div>
        <div className="admin-platform-live-preview-overlay">
          <strong>{displayName}</strong>
          <span>{displayCode}</span>
        </div>
      </div>
      <div className="admin-platform-live-preview-body">
        <strong>{displayName}</strong>
        <p>{displayDescription}</p>
      </div>
    </div>
  );
});

function PlatformEditorModal({
  editingPlatform,
  previewCacheBust,
  isSaving,
  saveError,
  onClearSaveError,
  onClose,
  onSave,
}) {
  const fileInputRef = useRef(null);
  const logoFileInputRef = useRef(null);
  const objectUrlRef = useRef('');
  const logoObjectUrlRef = useRef('');
  const cropCanvasRef = useRef(null);
  const screenshotImgRef = useRef(null);
  const screenshotZoomRef = useRef(1.1);
  const screenshotPanRef = useRef({ x: 0, y: 0 });
  const screenshotDragRef = useRef({ active: false, lastX: 0, lastY: 0 });
  const screenshotDrawRafRef = useRef(0);
  const [form, setForm] = useState(emptyForm);
  const [isDragging, setIsDragging] = useState(false);
  const [isLogoDragging, setIsLogoDragging] = useState(false);
  const [screenshotSourceUrl, setScreenshotSourceUrl] = useState('');
  const [screenshotZoom, setScreenshotZoom] = useState(1.1);
  const [cropReady, setCropReady] = useState(false);
  const [localError, setLocalError] = useState('');
  const isEdit = !!editingPlatform;
  const displayError = String(localError || saveError || '').trim();

  const syncPreviewFromCanvas = useCallback(() => {
    const canvas = cropCanvasRef.current;
    if (!canvas) return;
    try {
      const dataUrl = canvas.toDataURL('image/jpeg', 0.72);
      setForm((current) => ({ ...current, imagePreview: dataUrl }));
    } catch {
      // ignore preview sync failures
    }
  }, []);

  const scheduleScreenshotDraw = useCallback(() => {
    if (screenshotDrawRafRef.current) return;
    screenshotDrawRafRef.current = window.requestAnimationFrame(() => {
      screenshotDrawRafRef.current = 0;
      const canvas = cropCanvasRef.current;
      const img = screenshotImgRef.current;
      if (!canvas || !img) return;
      screenshotPanRef.current = clampCoverPan(canvas, img, screenshotZoomRef.current, screenshotPanRef.current);
      drawCoverCrop(canvas, img, screenshotZoomRef.current, screenshotPanRef.current);
      syncPreviewFromCanvas();
    });
  }, [syncPreviewFromCanvas]);

  useEffect(() => {
    if (objectUrlRef.current) {
      URL.revokeObjectURL(objectUrlRef.current);
      objectUrlRef.current = '';
    }
    if (logoObjectUrlRef.current) {
      URL.revokeObjectURL(logoObjectUrlRef.current);
      logoObjectUrlRef.current = '';
    }
    setScreenshotSourceUrl('');
    setCropReady(false);
    setScreenshotZoom(1.1);
    screenshotZoomRef.current = 1.1;
    screenshotPanRef.current = { x: 0, y: 0 };
    screenshotImgRef.current = null;
    setLocalError('');
    setForm(editingPlatform ? formFromPlatform(editingPlatform, previewCacheBust) : emptyForm());
    if (fileInputRef.current) fileInputRef.current.value = '';
    if (logoFileInputRef.current) logoFileInputRef.current.value = '';
  }, [editingPlatform, previewCacheBust]);

  useEffect(() => {
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, []);

  useEffect(() => () => {
    if (objectUrlRef.current) {
      URL.revokeObjectURL(objectUrlRef.current);
      objectUrlRef.current = '';
    }
    if (logoObjectUrlRef.current) {
      URL.revokeObjectURL(logoObjectUrlRef.current);
      logoObjectUrlRef.current = '';
    }
    if (screenshotDrawRafRef.current) {
      window.cancelAnimationFrame(screenshotDrawRafRef.current);
    }
  }, []);

  useEffect(() => {
    if (!screenshotSourceUrl) {
      screenshotImgRef.current = null;
      setCropReady(false);
      return;
    }

    let cancelled = false;
    const img = new Image();
    img.onload = () => {
      if (cancelled) return;
      screenshotImgRef.current = img;
      const canvas = cropCanvasRef.current;
      if (!canvas) return;
      canvas.width = SCREENSHOT_CROP_W;
      canvas.height = SCREENSHOT_CROP_H;
      screenshotPanRef.current = { x: 0, y: 0 };
      drawCoverCrop(canvas, img, screenshotZoomRef.current, screenshotPanRef.current);
      setCropReady(true);
      syncPreviewFromCanvas();
    };
    img.onerror = () => {
      if (cancelled) return;
      setCropReady(false);
      window.alert('Unable to load the selected image.');
    };
    img.src = screenshotSourceUrl;
    return () => {
      cancelled = true;
    };
  }, [screenshotSourceUrl, syncPreviewFromCanvas]);

  useEffect(() => {
    screenshotZoomRef.current = screenshotZoom;
    if (cropReady) scheduleScreenshotDraw();
  }, [screenshotZoom, cropReady, scheduleScreenshotDraw]);

  const onFormChange = useCallback((key, value) => {
    setForm((current) => {
      const next = { ...current, [key]: value };
      if (key === 'name' && !current.codeTouched) {
        next.code = slugifyPlatformCode(value);
      }
      if (key === 'code') {
        next.codeTouched = true;
        next.code = normalizePlatformCode(value);
      }
      return next;
    });
  }, []);

  const onPickImage = useCallback((event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const maxBytes = SCREENSHOT_MAX_MB * 1024 * 1024;
    if (file.size > maxBytes) {
      window.alert(`Screenshot must be ${SCREENSHOT_MAX_MB}MB or smaller.`);
      return;
    }

    if (objectUrlRef.current) {
      URL.revokeObjectURL(objectUrlRef.current);
      objectUrlRef.current = '';
    }
    const nextUrl = URL.createObjectURL(file);
    objectUrlRef.current = nextUrl;
    setScreenshotZoom(1.1);
    screenshotZoomRef.current = 1.1;
    screenshotPanRef.current = { x: 0, y: 0 };
    setScreenshotSourceUrl(nextUrl);
    setForm((current) => ({
      ...current,
      imageFile: file,
      imagePreview: nextUrl,
    }));
  }, []);

  const onClearImage = useCallback(() => {
    if (objectUrlRef.current) {
      URL.revokeObjectURL(objectUrlRef.current);
      objectUrlRef.current = '';
    }
    setScreenshotSourceUrl('');
    setCropReady(false);
    screenshotImgRef.current = null;
    setForm((current) => ({ ...current, imageFile: null, imagePreview: '' }));
    if (fileInputRef.current) fileInputRef.current.value = '';
  }, []);

  const onPickLogo = useCallback((event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const maxBytes = 2 * 1024 * 1024;
    if (file.size > maxBytes) {
      window.alert('Logo must be 2MB or smaller.');
      return;
    }

    setForm((current) => {
      if (logoObjectUrlRef.current) {
        URL.revokeObjectURL(logoObjectUrlRef.current);
        logoObjectUrlRef.current = '';
      }
      const nextUrl = URL.createObjectURL(file);
      logoObjectUrlRef.current = nextUrl;
      return {
        ...current,
        logoFile: file,
        logoPreview: nextUrl,
      };
    });
  }, []);

  const onClearLogo = useCallback(() => {
    setForm((current) => {
      if (logoObjectUrlRef.current) {
        URL.revokeObjectURL(logoObjectUrlRef.current);
        logoObjectUrlRef.current = '';
      }
      return { ...current, logoFile: null, logoPreview: '' };
    });
    if (logoFileInputRef.current) logoFileInputRef.current.value = '';
  }, []);

  const onDragOver = useCallback((event) => {
    event.preventDefault();
    setIsDragging(true);
  }, []);

  const onDragLeave = useCallback(() => {
    setIsDragging(false);
  }, []);

  const onDrop = useCallback((event) => {
    event.preventDefault();
    setIsDragging(false);
    const file = event.dataTransfer?.files?.[0];
    if (!file) return;
    onPickImage({ target: { files: [file] } });
  }, [onPickImage]);

  const onLogoDragOver = useCallback((event) => {
    event.preventDefault();
    setIsLogoDragging(true);
  }, []);

  const onLogoDragLeave = useCallback(() => {
    setIsLogoDragging(false);
  }, []);

  const onLogoDrop = useCallback((event) => {
    event.preventDefault();
    setIsLogoDragging(false);
    const file = event.dataTransfer?.files?.[0];
    if (!file) return;
    onPickLogo({ target: { files: [file] } });
  }, [onPickLogo]);

  const handleSave = useCallback(async () => {
    setLocalError('');
    if (typeof onClearSaveError === 'function') onClearSaveError();
    let nextForm = form;
    if (screenshotSourceUrl && screenshotImgRef.current) {
      try {
        const cropped = await exportScreenshotCrop(
          screenshotImgRef.current,
          screenshotZoomRef.current,
          screenshotPanRef.current
        );
        nextForm = { ...nextForm, imageFile: cropped };
      } catch (exportError) {
        setLocalError(exportError?.message || 'Unable to prepare screenshot.');
        return;
      }
    }
    if (form.logoFile) {
      try {
        const logoThumb = await exportLogoThumbnail(form.logoFile);
        nextForm = { ...nextForm, logoFile: logoThumb };
      } catch (logoError) {
        setLocalError(logoError?.message || 'Unable to prepare widget logo.');
        return;
      }
    }
    onSave(nextForm);
  }, [form, onClearSaveError, onSave, screenshotSourceUrl]);

  if (typeof document === 'undefined') return null;

  return createPortal(
    <div className="modal-backdrop admin-platform-modal-backdrop is-open" onClick={onClose}>
      <div
        className="modal-card admin-platform-editor-modal is-open"
        onClick={(event) => event.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="admin-platform-modal-title"
      >
        <header className="admin-platform-editor-header">
          <div>
            <h3 id="admin-platform-modal-title">{isEdit ? 'Edit Platform' : 'Add Platform'}</h3>
            <p className="admin-platform-editor-subtitle">
              {isEdit ? 'Update platform details for HK Support' : 'Create a new platform for HK Support'}
            </p>
          </div>
          <button type="button" className="admin-platform-editor-close" onClick={onClose} aria-label="Close">
            <X size={20} />
          </button>
        </header>

        <div className="admin-platform-editor-body">
          <div className="admin-platform-editor-preview-col">
            <section className="admin-platform-preview-section">
              <h4>Preview</h4>
              <p className="hint-text">This is how your platform card will look</p>
              <PlatformCardPreview
                name={form.name}
                code={form.code}
                description={form.description}
                imagePreview={form.imagePreview}
              />
            </section>

            <section
              className={`admin-platform-upload-zone${isDragging ? ' is-dragging' : ''}${form.imagePreview ? ' has-image' : ''}`}
              onDragOver={onDragOver}
              onDragLeave={onDragLeave}
              onDrop={onDrop}
            >
              <input ref={fileInputRef} type="file" accept="image/png,image/jpeg,image/webp" onChange={onPickImage} />
              <Upload size={22} />
              <strong>Upload screenshot</strong>
              <span className="hint-text">PNG, JPG, WebP up to {SCREENSHOT_MAX_MB}MB · cropped & compressed on save</span>
              <span className="admin-platform-upload-or">or drag and drop</span>
              <div className="admin-platform-upload-actions">
                <button type="button" className="admin-platform-file-action admin-platform-file-choose" onClick={() => fileInputRef.current?.click()}>
                  Choose file
                </button>
                {form.imagePreview && (
                  <button type="button" className="admin-platform-file-action admin-platform-file-remove" onClick={onClearImage}>
                    Remove
                  </button>
                )}
              </div>
              {screenshotSourceUrl && (
                <>
                  <div className="admin-platform-screenshot-crop-wrap">
                    <canvas
                      ref={cropCanvasRef}
                      className="admin-platform-screenshot-crop-canvas"
                      width={SCREENSHOT_CROP_W}
                      height={SCREENSHOT_CROP_H}
                      onPointerDown={(event) => {
                        const canvas = cropCanvasRef.current;
                        const img = screenshotImgRef.current;
                        if (!canvas || !img) return;
                        screenshotDragRef.current.active = true;
                        screenshotDragRef.current.lastX = event.clientX;
                        screenshotDragRef.current.lastY = event.clientY;
                        canvas.setPointerCapture(event.pointerId);
                      }}
                      onPointerMove={(event) => {
                        const drag = screenshotDragRef.current;
                        if (!drag.active) return;
                        const canvas = cropCanvasRef.current;
                        if (!canvas) return;
                        const rect = canvas.getBoundingClientRect();
                        const scaleX = rect.width > 0 ? canvas.width / rect.width : 1;
                        const scaleY = rect.height > 0 ? canvas.height / rect.height : 1;
                        const dx = (event.clientX - drag.lastX) * scaleX;
                        const dy = (event.clientY - drag.lastY) * scaleY;
                        drag.lastX = event.clientX;
                        drag.lastY = event.clientY;
                        screenshotPanRef.current = {
                          x: screenshotPanRef.current.x + dx,
                          y: screenshotPanRef.current.y + dy,
                        };
                        scheduleScreenshotDraw();
                      }}
                      onPointerUp={(event) => {
                        const canvas = cropCanvasRef.current;
                        if (canvas && canvas.hasPointerCapture(event.pointerId)) {
                          canvas.releasePointerCapture(event.pointerId);
                        }
                        screenshotDragRef.current.active = false;
                      }}
                      onPointerCancel={(event) => {
                        const canvas = cropCanvasRef.current;
                        if (canvas && canvas.hasPointerCapture(event.pointerId)) {
                          canvas.releasePointerCapture(event.pointerId);
                        }
                        screenshotDragRef.current.active = false;
                      }}
                    />
                  </div>
                  <div className="admin-platform-screenshot-zoom-row">
                    <label htmlFor="platformScreenshotZoom">Scale · drag to reposition</label>
                    <input
                      id="platformScreenshotZoom"
                      type="range"
                      min="1"
                      max="2.4"
                      step="0.01"
                      value={screenshotZoom}
                      onChange={(event) => setScreenshotZoom(Number(event.target.value))}
                    />
                  </div>
                  <p className="admin-platform-screenshot-crop-hint">
                    Fits the card frame (2:1). Zoom and drag like an avatar — save compresses to a card thumbnail.
                  </p>
                </>
              )}
            </section>

            <section
              className={`admin-platform-upload-zone admin-platform-logo-upload-zone${isLogoDragging ? ' is-dragging' : ''}${form.logoPreview ? ' has-image' : ''}`}
              onDragOver={onLogoDragOver}
              onDragLeave={onLogoDragLeave}
              onDrop={onLogoDrop}
            >
              <input ref={logoFileInputRef} type="file" accept="image/png,image/jpeg,image/webp" onChange={onPickLogo} />
              {form.logoPreview ? (
                <img src={form.logoPreview} alt="" className="admin-platform-logo-upload-preview" />
              ) : (
                <Upload size={20} />
              )}
              <strong>Upload widget logo</strong>
              <span className="hint-text">Square icon for app launcher · auto 128px PNG thumbnail</span>
              <span className="admin-platform-upload-or">or drag and drop</span>
              <div className="admin-platform-upload-actions">
                <button type="button" className="admin-platform-file-action admin-platform-file-choose" onClick={() => logoFileInputRef.current?.click()}>
                  Choose file
                </button>
                {form.logoPreview && (
                  <button type="button" className="admin-platform-file-action admin-platform-file-remove" onClick={onClearLogo}>
                    Remove
                  </button>
                )}
              </div>
            </section>
          </div>

          <section className="admin-platform-editor-form-col">
            <h4>Platform Details</h4>

            <label className="admin-platform-field-label">
              Platform Name
              <input
                className="admin-platform-input"
                value={form.name}
                onChange={(event) => onFormChange('name', event.target.value)}
                placeholder="e.g. HKDK Claim Portal"
                maxLength={256}
              />
            </label>

            <label className="admin-platform-field-label">
              Code
              <input
                className={`admin-platform-input${form.codeTouched && form.code && !isValidPlatformCode(form.code) ? ' input-invalid' : ''}`}
                value={form.code}
                onChange={(event) => onFormChange('code', event.target.value)}
                onBlur={() => onFormChange('code', form.code)}
                placeholder="e.g. portal2"
                maxLength={64}
              />
              <span className="hint-text admin-platform-code-hint">
                1–64 characters: a–z, 0–9, hyphen, underscore. Screenshot: <code>{screenshotFileName(form.code)}</code> · Logo: <code>{logoFileName(form.code)}</code>
              </span>
            </label>

            <label className="admin-platform-field-label">
              Default URL
              <input
                className="admin-platform-input"
                value={form.url}
                onChange={(event) => onFormChange('url', event.target.value)}
                placeholder="https://example.com"
                maxLength={2000}
              />
              <span className="hint-text admin-platform-code-hint">
                Fallback when no company (DataAreaId) URL matches the user.
              </span>
            </label>

            <div className="admin-platform-data-area-section">
              <div className="admin-platform-data-area-head">
                <strong>Company URLs</strong>
                <button
                  type="button"
                  className="admin-platform-data-area-add"
                  onClick={() =>
                    setForm((current) => ({
                      ...current,
                      dataAreaUrls: [...(current.dataAreaUrls || []), { dataAreaId: '', url: '' }],
                    }))
                  }
                >
                  <Plus size={14} />
                  Add company
                </button>
              </div>
              {(form.dataAreaUrls || []).length === 0 ? (
                <p className="hint-text">No company-specific URLs. Users launch with the default URL.</p>
              ) : (
                <div className="admin-platform-data-area-list">
                  {(form.dataAreaUrls || []).map((row, index) => (
                    <div key={`da-${index}`} className="admin-platform-data-area-row">
                      <input
                        className="admin-platform-input admin-platform-data-area-id"
                        value={row.dataAreaId}
                        onChange={(event) => {
                          const value = event.target.value;
                          setForm((current) => {
                            const next = [...(current.dataAreaUrls || [])];
                            next[index] = { ...next[index], dataAreaId: value };
                            return { ...current, dataAreaUrls: next };
                          });
                        }}
                        placeholder="DataAreaId (e.g. dk)"
                        maxLength={16}
                        aria-label={`DataAreaId ${index + 1}`}
                      />
                      <input
                        className="admin-platform-input admin-platform-data-area-url"
                        value={row.url}
                        onChange={(event) => {
                          const value = event.target.value;
                          setForm((current) => {
                            const next = [...(current.dataAreaUrls || [])];
                            next[index] = { ...next[index], url: value };
                            return { ...current, dataAreaUrls: next };
                          });
                        }}
                        placeholder="https://company.example.com"
                        maxLength={2000}
                        aria-label={`URL for company ${index + 1}`}
                      />
                      <button
                        type="button"
                        className="admin-platform-data-area-remove"
                        onClick={() =>
                          setForm((current) => ({
                            ...current,
                            dataAreaUrls: (current.dataAreaUrls || []).filter((_, i) => i !== index),
                          }))
                        }
                        aria-label="Remove company URL"
                      >
                        <Trash2 size={15} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="admin-platform-description-field">
              <label className="admin-platform-field-label" htmlFor="admin-platform-description">
                Description <span className="admin-platform-optional">(optional)</span>
              </label>
              <textarea
                id="admin-platform-description"
                className="admin-platform-description-input"
                value={form.description}
                onChange={(event) => onFormChange('description', event.target.value)}
                placeholder="Short description shown on the Platforms page"
                rows={4}
                maxLength={DESCRIPTION_MAX}
              />
              <span className="admin-platform-char-count">
                {form.description.length} / {DESCRIPTION_MAX}
              </span>
            </div>

            <div className="admin-platform-visibility-row">
              <div>
                <strong>Show on Platforms page</strong>
                <p className="hint-text">Make this platform visible on the public Platforms page</p>
              </div>
              <button
                type="button"
                role="switch"
                aria-checked={form.isActive}
                className={`admin-platform-switch${form.isActive ? ' is-on' : ''}`}
                onClick={() => onFormChange('isActive', !form.isActive)}
              >
                <span className="admin-platform-switch-thumb" />
              </button>
            </div>
          </section>
        </div>

        <footer className="admin-platform-editor-footer">
          {displayError ? (
            <p className="error-text admin-platform-editor-error" role="alert">
              {displayError}
            </p>
          ) : null}
          <div className="admin-platform-editor-footer-actions">
            <button type="button" className="ghost-button admin-inline-button" onClick={onClose} disabled={isSaving}>
              Cancel
            </button>
            <button type="button" className="primary-button admin-inline-button admin-platform-save-button" onClick={handleSave} disabled={isSaving}>
              {isSaving ? 'Saving...' : isEdit ? 'Save changes' : 'Save Platform'}
            </button>
          </div>
        </footer>
      </div>
    </div>,
    document.body
  );
}

function PlatformDeleteModal({ platform, isDeleting, onClose, onConfirm }) {
  useEffect(() => {
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, []);

  if (!platform || typeof document === 'undefined') return null;

  return createPortal(
    <div className="modal-backdrop admin-platform-modal-backdrop is-open" onClick={onClose}>
      <div
        className="modal-card admin-platform-delete-dialog is-open"
        onClick={(event) => event.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="admin-platform-delete-title"
      >
        <div className="admin-platform-delete-dialog-header">
          <h3 id="admin-platform-delete-title">Delete {platform.name}?</h3>
          <button type="button" className="admin-platform-editor-close" onClick={onClose} aria-label="Close" disabled={isDeleting}>
            <X size={18} />
          </button>
        </div>
        <p className="hint-text">
          If this platform has access assignments, it will be deactivated instead of permanently deleted.
        </p>
        <div className="admin-platform-delete-dialog-actions">
          <button type="button" className="ghost-button admin-inline-button" onClick={onClose} disabled={isDeleting}>
            Cancel
          </button>
          <button
            type="button"
            className="primary-button admin-inline-button admin-platform-delete-button"
            onClick={onConfirm}
            disabled={isDeleting}
          >
            {isDeleting ? 'Deleting...' : 'Delete platform'}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}

const AdminPlatformCard = React.memo(function AdminPlatformCard({
  platform,
  previewCacheBust,
  isDeleting,
  onEdit,
  onDelete,
  index = 0,
}) {
  return (
    <motion.article
      className={`admin-platform-card${platform.isActive ? '' : ' is-inactive'}`}
      initial={{ opacity: 0, y: -20, scale: 0.98 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      transition={revealItemTransition(index, { duration: 0.4, step: 0.055, cap: 12 })}
    >
      <div className="admin-platform-card-media">
        <PlatformScreenshotImage platform={platform} cacheBust={previewCacheBust} />
        {!platform.isActive && <span className="admin-platform-status-badge">Inactive</span>}
      </div>
      <div className="admin-platform-card-body">
        <div className="admin-platform-card-head">
          <h3>{platform.name}</h3>
          <code className="admin-platform-code">{platform.code}</code>
        </div>
        <p className="admin-platform-description">{platform.description || 'No description provided.'}</p>
        {platform.url ? (
          <a className="admin-platform-link" href={platform.url} target="_blank" rel="noreferrer">
            <ExternalLink size={14} />
            <span>{platform.url}</span>
          </a>
        ) : (
          <p className="hint-text">No default URL configured</p>
        )}
        {Array.isArray(platform.dataAreaUrls) && platform.dataAreaUrls.length > 0 && (
          <p className="hint-text admin-platform-data-area-summary">
            {platform.dataAreaUrls.length} company URL{platform.dataAreaUrls.length === 1 ? '' : 's'}:{' '}
            {platform.dataAreaUrls.map((x) => x.dataAreaId).filter(Boolean).join(', ')}
          </p>
        )}
        <div className="admin-platform-card-actions">
          <button type="button" className="ghost-button admin-inline-button" onClick={() => onEdit(platform)}>
            <Pencil size={14} />
            Edit
          </button>
          <button
            type="button"
            className="ghost-button admin-inline-button admin-platform-delete-button"
            onClick={() => onDelete(platform)}
            disabled={isDeleting}
          >
            <Trash2 size={14} />
            Delete
          </button>
        </div>
      </div>
    </motion.article>
  );
});

export function AdminPlatformsTab() {
  const [platforms, setPlatforms] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [editorOpen, setEditorOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [editingPlatform, setEditingPlatform] = useState(null);
  const [deletingPlatform, setDeletingPlatform] = useState(null);
  const [previewCacheBust, setPreviewCacheBust] = useState(() => String(Date.now()));

  const loadPlatforms = useCallback(async ({ silent } = { silent: false }) => {
    if (!silent) setIsLoading(true);
    setError('');
    try {
      const response = await fetch(CATALOG_URL, { credentials: 'include' });
      if (!response.ok) {
        const maybeError = await response.json().catch(() => null);
        throw new Error(maybeError?.message || 'Failed to load platforms.');
      }
      const data = await response.json();
      setPlatforms(Array.isArray(data) ? data : []);
    } catch (loadError) {
      if (!silent) setError(loadError.message || 'Failed to load platforms.');
    } finally {
      if (!silent) setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadPlatforms();
  }, [loadPlatforms]);

  const sortedPlatforms = useMemo(
    () => [...platforms].sort((left, right) => String(left?.name || '').localeCompare(String(right?.name || ''), undefined, { sensitivity: 'base' })),
    [platforms]
  );

  const onOpenCreate = useCallback(() => {
    setEditingPlatform(null);
    setEditorOpen(true);
    setSuccess('');
    setError('');
  }, []);

  const onOpenEdit = useCallback((platform) => {
    setEditingPlatform(platform);
    setEditorOpen(true);
    setSuccess('');
    setError('');
  }, []);

  const onCloseEditor = useCallback(() => {
    if (isSaving) return;
    setEditorOpen(false);
    setEditingPlatform(null);
  }, [isSaving]);

  const onOpenDelete = useCallback((platform) => {
    setDeletingPlatform(platform);
    setDeleteOpen(true);
    setSuccess('');
    setError('');
  }, []);

  const onCloseDelete = useCallback(() => {
    if (isDeleting) return;
    setDeleteOpen(false);
    setDeletingPlatform(null);
  }, [isDeleting]);

  const uploadScreenshot = useCallback(async (platformId, imageFile) => {
    const body = new FormData();
    body.append('file', imageFile);
    const response = await fetch(`${CATALOG_URL}/${platformId}/screenshot`, {
      method: 'POST',
      credentials: 'include',
      body,
    });
    if (!response.ok) {
      const maybeError = await response.json().catch(() => null);
      throw new Error(maybeError?.message || 'Screenshot upload failed.');
    }
  }, []);

  const uploadLogo = useCallback(async (platformId, imageFile) => {
    const body = new FormData();
    body.append('file', imageFile);
    const response = await fetch(`${CATALOG_URL}/${platformId}/logo`, {
      method: 'POST',
      credentials: 'include',
      body,
    });
    if (!response.ok) {
      const maybeError = await response.json().catch(() => null);
      throw new Error(maybeError?.message || 'Logo upload failed.');
    }
  }, []);

  const invalidateToolsCache = useCallback(() => {
    clearPortalQueryCache();
    setPreviewCacheBust(String(Date.now()));
  }, []);

  const onSavePlatform = useCallback(async (form) => {
    const dataAreaUrls = (form.dataAreaUrls || [])
      .map((row) => ({
        dataAreaId: String(row?.dataAreaId || '').trim(),
        url: String(row?.url || '').trim(),
      }))
      .filter((row) => row.dataAreaId || row.url);

    for (const row of dataAreaUrls) {
      if (!row.dataAreaId || !row.url) {
        setError('Each company row needs both DataAreaId and URL.');
        return;
      }
      if (row.dataAreaId.length > 16) {
        setError('DataAreaId must be 16 characters or fewer.');
        return;
      }
    }

    const payload = {
      name: form.name.trim(),
      code: normalizePlatformCode(form.code),
      description: form.description.trim() || null,
      url: form.url.trim() || null,
      isActive: !!form.isActive,
      dataAreaUrls,
    };

    if (!payload.name) {
      setError('Platform name is required.');
      return;
    }
    if (!payload.code) {
      setError('Platform code is required.');
      return;
    }
    if (!isValidPlatformCode(payload.code)) {
      setError('Platform code must be 1–64 characters: a–z, 0–9, hyphen or underscore.');
      return;
    }

    setIsSaving(true);
    setError('');
    setSuccess('');
    try {
      const isEdit = !!editingPlatform?.id;
      const response = await fetch(isEdit ? `${CATALOG_URL}/${editingPlatform.id}` : CATALOG_URL, {
        method: isEdit ? 'PUT' : 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!response.ok) {
        const maybeError = await response.json().catch(() => null);
        throw new Error(maybeError?.message || 'Failed to save platform.');
      }
      const saved = await response.json();
      if (form.imageFile) {
        await uploadScreenshot(saved.id, form.imageFile);
      }
      if (form.logoFile) {
        await uploadLogo(saved.id, form.logoFile);
      }
      await loadPlatforms({ silent: true });
      invalidateToolsCache();
      setSuccess(isEdit ? `Platform "${saved.name}" updated.` : `Platform "${saved.name}" created.`);
      setEditorOpen(false);
      setEditingPlatform(null);
    } catch (saveError) {
      setError(saveError.message || 'Failed to save platform.');
    } finally {
      setIsSaving(false);
    }
  }, [editingPlatform, invalidateToolsCache, loadPlatforms, uploadLogo, uploadScreenshot]);

  const onConfirmDelete = useCallback(async () => {
    if (!deletingPlatform?.id) return;
    setIsDeleting(true);
    setError('');
    setSuccess('');
    try {
      const response = await fetch(`${CATALOG_URL}/${deletingPlatform.id}`, {
        method: 'DELETE',
        credentials: 'include',
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(data?.message || 'Failed to delete platform.');
      }
      await loadPlatforms({ silent: true });
      invalidateToolsCache();
      setSuccess(data?.message || `Platform "${deletingPlatform.name}" removed.`);
      setDeleteOpen(false);
      setDeletingPlatform(null);
    } catch (deleteError) {
      setError(deleteError.message || 'Failed to delete platform.');
    } finally {
      setIsDeleting(false);
    }
  }, [deletingPlatform, invalidateToolsCache, loadPlatforms]);

  return (
    <>
      <div className="admin-platforms-toolbar">
        <p className="hint-text admin-platforms-hint">
          Manage platform cards on the Platforms page (screenshot) and launcher widget icons (logo).
        </p>
        <button type="button" className="primary-button admin-inline-button" onClick={onOpenCreate}>
          <Plus size={16} />
          Add Platform
        </button>
      </div>

      {error && !editorOpen && !deleteOpen && <p className="error-text">{error}</p>}
      {success && !editorOpen && <p className="success-text">{success}</p>}

      {isLoading && platforms.length === 0 ? (
        <div className="portal-loading-inline">Loading platforms...</div>
      ) : sortedPlatforms.length === 0 ? (
        <div className="admin-platforms-empty">
          <Layers size={28} />
          <p>No platforms yet. Add your first platform to show it on the Platforms page.</p>
        </div>
      ) : (
        <div className="admin-platforms-grid">
          {sortedPlatforms.map((platform, index) => (
            <AdminPlatformCard
              key={platform.id}
              platform={platform}
              index={index}
              previewCacheBust={previewCacheBust}
              isDeleting={isDeleting}
              onEdit={onOpenEdit}
              onDelete={onOpenDelete}
            />
          ))}
        </div>
      )}

      {editorOpen && (
        <PlatformEditorModal
          editingPlatform={editingPlatform}
          previewCacheBust={previewCacheBust}
          isSaving={isSaving}
          saveError={error}
          onClearSaveError={() => setError('')}
          onClose={onCloseEditor}
          onSave={onSavePlatform}
        />
      )}

      {deleteOpen && deletingPlatform && (
        <PlatformDeleteModal
          platform={deletingPlatform}
          isDeleting={isDeleting}
          onClose={onCloseDelete}
          onConfirm={onConfirmDelete}
        />
      )}
    </>
  );
}
